import React from 'react';
import Select, { components } from 'react-select';
import { ReceivedDate } from './DatePicker';
import * as HELPER from '../config/helper';

interface PaymentProof {
  doc_path?: string;
  original_url?: string;
  file?: File;
}

export interface AdditionalPayment {
  id: string;
  payment_made_from: number | string | null;
  payment_made_to: number | string | null;
  payment_amount_received: string;
  payment_received_date: string | null;
  excess_amount_with_oto?: number | string;
  proof_of_payment?: PaymentProof;
  is_saved?: boolean;
}

interface AdditionalPaymentSectionProps {
  title?: string;
  startIndex?: number;
  payments: AdditionalPayment[];
  paymentMadeFromOptions: any[];
  paidToOptions: any[];
  errors: Record<string, string>;
  isDisabled?: boolean;
  onChange?: (index: number, field: keyof AdditionalPayment, value: any) => void;
  onFileUpload?: (index: number, file: File) => void;
  onRemoveFile?: (index: number) => void;
  onRemovePayment?: (index: number) => void;
  onSave?: () => void;
  getExcessAmountAfterPayment: (index: number) => number | string;
  getFileName: (doc: PaymentProof) => string;
}

const { ValueContainer, Placeholder } = components;

const CustomValueContainer = (props: any) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(props.children, (child: any) =>
        child && child.type !== Placeholder ? child : null
      )}
    </ValueContainer>
  );
};

const getSelectedOption = (options: any[], value: any) => {
  if (value === null || value === undefined || value === '') return null;
  return options.find((option: any) => String(option?.value) === String(value)) || null;
};

const toAmount = (value: any) => {
  const amount = Number(HELPER.normalizeIdDecimalAmount(value));
  return Number.isFinite(amount) ? amount : 0;
};

const formatCurrencyValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '';
  const amount = toAmount(value);
  const formattedAmount = HELPER.addCurrencyFormatting(Math.abs(amount));
  return amount < 0 ? `- ${formattedAmount}` : formattedAmount;
};

const formatCurrencyInputValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '';
  return HELPER.addIdDecimalCurrencyFormatting(value);
};

const AdditionalPaymentSection: React.FC<AdditionalPaymentSectionProps> = ({
  title = 'Additional Payment',
  startIndex = 0,
  payments,
  paymentMadeFromOptions,
  paidToOptions,
  errors,
  isDisabled = false,
  onChange,
  onFileUpload,
  onRemoveFile,
  onRemovePayment,
  onSave,
  getExcessAmountAfterPayment,
  getFileName,
}) => {
  if (!payments.length) return null;

  return (
    <div className="additional-payment-section" aria-label={title}>
      {payments.map((payment, index) => {
        const paymentProof = payment.proof_of_payment;
        const excessAmount = getExcessAmountAfterPayment(index);

        return (
          <div className="additional-payment-card" key={payment.id}>
            <div className="additional-payment-card-header">
              <h2>Payment {startIndex + index + 1}</h2>
              {!isDisabled && onRemovePayment && (
                <button
                  type="button"
                  className="close_icn"
                  aria-label="Remove payment"
                  onClick={() => onRemovePayment(index)}
                >
                  <i className="ic-clearclose"></i>
                </button>
              )}
            </div>
            <div className="additional-payment-block">
              <div className="additional-payment-block-title">Payment Details</div>
              <div className="row additional-payment-fields-row">
                <fieldset className="single-select col-md-3">
                  <div className="material">
                    <Select
                      components={{ ValueContainer: CustomValueContainer }}
                      options={paymentMadeFromOptions}
                      placeholder="Payment Made From *"
                      className="react-select"
                      classNamePrefix="react-select"
                      value={getSelectedOption(paymentMadeFromOptions, payment.payment_made_from)}
                      onChange={(option: any) => onChange?.(index, 'payment_made_from', option?.value)}
                      isDisabled={isDisabled}
                    />
                    {errors[`payment_made_from_${payment.id}`] && (
                      <span className="error">{errors[`payment_made_from_${payment.id}`]}</span>
                    )}
                  </div>
                </fieldset>
                <fieldset className="single-select col-md-3">
                  <div className="material">
                    <Select
                      components={{ ValueContainer: CustomValueContainer }}
                      options={paidToOptions}
                      placeholder="Payment Made To *"
                      className="react-select"
                      classNamePrefix="react-select"
                      value={getSelectedOption(paidToOptions, payment.payment_made_to)}
                      onChange={(option: any) => onChange?.(index, 'payment_made_to', option?.value)}
                      isDisabled={isDisabled}
                    />
                    {errors[`payment_made_to_${payment.id}`] && (
                      <span className="error">{errors[`payment_made_to_${payment.id}`]}</span>
                    )}
                  </div>
                </fieldset>
                <fieldset className="col-md-3">
                  <div className="material">
                    <input
                      spellCheck={false}
                      type="text"
                      inputMode="decimal"
                      placeholder=" "
                      name={`payment_amount_received_${payment.id}`}
                      className="form-input"
                      value={formatCurrencyInputValue(payment.payment_amount_received)}
                      onChange={(event) => onChange?.(
                        index,
                        'payment_amount_received',
                        HELPER.normalizeIdDecimalAmount(event.target.value, true)
                      )}
                      disabled={isDisabled}
                    />
                    <label data-label="Payment Amount Received *" className="form-label"></label>
                    {errors[`payment_amount_received_${payment.id}`] && (
                      <div className="error text-danger mb-2">
                        <div>{errors[`payment_amount_received_${payment.id}`]}</div>
                      </div>
                    )}
                  </div>
                </fieldset>
                <fieldset className="col-md-3 receiveDateShow">
                  <div className="material">
                    <ReceivedDate
                      placeholder="Payment Received Date *"
                      selectedDate={payment.payment_received_date ? new Date(payment.payment_received_date) : null}
                      onDateChange={(date) => onChange?.(index, 'payment_received_date', date?.toISOString() || null)}
                      disabled={isDisabled}
                      maxDate={new Date()}
                    />
                    {errors[`payment_received_date_${payment.id}`] && (
                      <span className="error">{errors[`payment_received_date_${payment.id}`]}</span>
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
            <div className="additional-payment-block additional-payment-proof-block">
              <div className="additional-payment-block-title">Verification &amp; Proof</div>
              <div className="row additional-payment-proof-row">
                <div className="col-md-3">
                  <div className="additional-payment-excess">
                    <span>Excess Amount with OTO</span>
                    <strong className={toAmount(excessAmount) < 0 ? 'negative' : ''}>
                      {formatCurrencyValue(excessAmount)}
                    </strong>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className={`additional-payment-upload ${isDisabled ? 'disabled' : ''}`}>
                    {!isDisabled && (
                      <input
                        type="file"
                        name={`proof_of_payment_${payment.id}`}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) onFileUpload?.(index, file);
                          event.target.value = '';
                        }}
                      />
                    )}
                    {paymentProof?.doc_path ? (
                      <div className="uploaded-file">
                        <div className="upload-file-name">
                          <a href={paymentProof.doc_path} target="_blank" rel="noopener noreferrer">
                            {getFileName(paymentProof)}
                          </a>
                        </div>
                        {!isDisabled && onRemoveFile && (
                          <button
                            type="button"
                            className="additional-payment-file-remove"
                            aria-label="Remove proof"
                            onClick={() => onRemoveFile(index)}
                          >
                            <i className="ic-clearclose"></i>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="additional-payment-upload-placeholder">
                        <i className="ic-upload"></i>
                        <p>Click to upload proof</p>
                      </div>
                    )}
                  </div>
                  {errors[`proof_of_payment_${payment.id}`] && (
                    <span className="error">{errors[`proof_of_payment_${payment.id}`]}</span>
                  )}
                </div>
              </div>
            </div>
            {!isDisabled && onSave && (
              <button type="button" className="btn-primary additional-payment-save-btn" onClick={onSave}>
                Save Payment
              </button>
            )}
            {isDisabled && (
              <button type="button" className="additional-payment-recorded-btn" disabled>
                <i className="ic-check"></i>
                Payment Recorded
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdditionalPaymentSection;
